import React, { useEffect, useMemo, useState } from "react";
import "./MemberManagementCard.css";
import { API_BASE } from "../api/config";

const MEMBER_API_BASE = String(API_BASE).replace(/\/+$/, "");
const ADMIN_SECRET = String(process.env.REACT_APP_MEMBER_ADMIN_SECRET || process.env.REACT_APP_SALE_V2_SECRET || "").trim();

function authHeaders(extra = {}) {
  const headers = { ...extra };
  if (ADMIN_SECRET) {
    headers["x-member-admin-secret"] = ADMIN_SECRET;
    headers["x-sale-v2-secret"] = ADMIN_SECRET;
  }
  return headers;
}

async function apiFetch(path, options = {}) {
  const res = await fetch(MEMBER_API_BASE + path, {
    ...options,
    headers: authHeaders(options.headers || {}),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json.success === false) {
    throw new Error(json.message || json.error || "Request failed");
  }

  return json;
}

function copyText(text) {
  navigator.clipboard?.writeText(String(text || ""));
}

function normalizePhone(value) {
  return String(value || "").replace(/[\s\-()]/g, "").trim();
}

function normalizeText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function splitExcelLine(line) {
  if (line.includes("\t")) return line.split("\t");
  return line.split(",");
}

function normalizeHeader(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_\-]+/g, "");
}

function parseExcelText(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) return [];

  const first = splitExcelLine(lines[0]).map(normalizeHeader);
  const known = new Set([
    "membercode",
    "code",
    "name",
    "nrc",
    "phone",
    "email",
    "address",
    "discount",
    "discountpercent",
    "note",
  ]);

  const hasHeader = first.some((h) => known.has(h));
  const headers = hasHeader
    ? first
    : ["membercode", "name", "nrc", "phone", "email", "address", "discountpercent", "note"];

  const bodyLines = hasHeader ? lines.slice(1) : lines;

  return bodyLines.map((line) => {
    const cells = splitExcelLine(line).map((cell) => cell.trim());
    const obj = {};

    headers.forEach((header, index) => {
      obj[header] = cells[index] || "";
    });

    return {
      memberCode: obj.membercode || obj.code || "",
      name: obj.name || "",
      nrc: obj.nrc || "",
      phone: normalizePhone(obj.phone || ""),
      email: String(obj.email || "").trim().toLowerCase(),
      address: obj.address || "",
      discountPercent: obj.discountpercent || obj.discount || "10",
      note: obj.note || "",
    };
  });
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString();
}

function formatDateTime(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

const emptyMemberForm = {
  memberCode: "",
  name: "",
  nrc: "",
  phone: "",
  email: "",
  address: "",
  discountPercent: "10",
  note: "",
};

export default function MemberManagementCard() {
  const [activeTab, setActiveTab] = useState("members");
  const [members, setMembers] = useState([]);
  const [qrRows, setQrRows] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const [memberForm, setMemberForm] = useState(emptyMemberForm);
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState(emptyMemberForm);
  const [shopCode, setShopCode] = useState("DNGHT");
  const [lastQrLink, setLastQrLink] = useState("");

  const [excelText, setExcelText] = useState(
`memberCode\tname\tnrc\tphone\temail\taddress\tdiscountPercent\tnote
MEM-001\tAye Aye\t12/YATAYA(N)123456\t09123456789\taye@example.com\tYangon\t10\tVIP`
  );

  const [historySearch, setHistorySearch] = useState("");

  const excelRows = useMemo(() => parseExcelText(excelText), [excelText]);

  const loadMembers = async () => {
    const json = await apiFetch("/api/erp/members-plus?status=ALL&limit=500");
    setMembers(Array.isArray(json.rows) ? json.rows : []);
  };

  const loadQrRows = async () => {
    const json = await apiFetch("/api/erp/member-qr/list");
    setQrRows(
      Array.isArray(json.rows)
        ? json.rows.map((row) => ({
            ...row,
            shopCode: row.shopCode || row.shop_code || "",
            qrLink: row.qrLink || row.qr_link || "",
          }))
        : [],
    );
  };

  const loadHistory = async (searchValue = historySearch) => {
    const q = encodeURIComponent(String(searchValue || "").trim());
    const json = await apiFetch("/api/erp/member-purchase-history?limit=300&search=" + q);
    setHistoryRows(Array.isArray(json.rows) ? json.rows : []);
  };

  const refreshAll = async () => {
    setLoading(true);
    setMsg("");

    try {
      await Promise.all([loadMembers(), loadQrRows()]);
      if (activeTab === "history") await loadHistory();
    } catch (err) {
      setMsg("❌ " + (err.message || "Load failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateMemberForm = (key, value) => {
    setMemberForm((prev) => ({ ...prev, [key]: value }));
  };

  const startEditMember = (member) => {
    setEditingMember(member);
    setEditForm({
      memberCode: member.memberCode || "",
      name: member.name || "",
      nrc: member.nrc || "",
      phone: member.phone || "",
      email: member.email || "",
      address: member.address || "",
      discountPercent: String(member.discountPercent ?? 10),
      note: member.note || "",
    });
  };

  const updateEditForm = (key, value) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const cancelEditMember = () => {
    setEditingMember(null);
    setEditForm(emptyMemberForm);
  };

  const saveEditMember = async (e) => {
    e.preventDefault();

    if (!editingMember?.id) {
      setMsg("❌ Member id missing");
      return;
    }

    setLoading(true);
    setMsg("");

    try {
      await apiFetch("/api/erp/members-plus/" + encodeURIComponent(editingMember.id), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...editForm,
          phone: normalizePhone(editForm.phone),
          name: normalizeText(editForm.name),
          nrc: normalizeText(editForm.nrc),
          address: normalizeText(editForm.address),
        }),
      });

      setMsg("✅ Member updated successfully");
      cancelEditMember();
      await loadMembers();
    } catch (err) {
      setMsg("❌ " + (err.message || "Update member failed"));
    } finally {
      setLoading(false);
    }
  };

  const createMember = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg("");

    try {
      await apiFetch("/api/erp/members-plus", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...memberForm,
          phone: normalizePhone(memberForm.phone),
          name: normalizeText(memberForm.name),
          nrc: normalizeText(memberForm.nrc),
          address: normalizeText(memberForm.address),
          createdBy: "ERP",
        }),
      });

      setMsg("✅ Member created successfully");
      setMemberForm(emptyMemberForm);
      await loadMembers();
    } catch (err) {
      setMsg("❌ " + (err.message || "Create member failed"));
    } finally {
      setLoading(false);
    }
  };

  const importExcelRows = async () => {
    setLoading(true);
    setMsg("");

    try {
      const validRows = excelRows.filter((row) => row.name && row.nrc && row.phone && row.email);

      if (!validRows.length) {
        throw new Error("No valid rows. Need name, NRC, phone, and verification email.");
      }

      const json = await apiFetch("/api/erp/members/import-excel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rows: validRows, createdBy: "ERP_EXCEL_IMPORT" }),
      });

      setMsg(
        "✅ Imported: " +
          Number(json.importedCount || 0) +
          " | Skipped: " +
          Number(json.skippedCount || 0),
      );

      await loadMembers();
      setActiveTab("members");
    } catch (err) {
      setMsg("❌ " + (err.message || "Import failed"));
    } finally {
      setLoading(false);
    }
  };

  const changeStatus = async (member, status) => {
    setLoading(true);
    setMsg("");

    try {
      await apiFetch("/api/erp/members-plus/" + encodeURIComponent(member.id) + "/status", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });

      setMsg("✅ Member status updated");
      await loadMembers();
    } catch (err) {
      setMsg("❌ " + (err.message || "Status update failed"));
    } finally {
      setLoading(false);
    }
  };

  const generateQr = async (e) => {
    e.preventDefault();

    const cleanShop = String(shopCode || "").trim().toUpperCase();

    if (!cleanShop) {
      setMsg("❌ Shop code required");
      return;
    }

    setLoading(true);
    setMsg("");

    try {
      const json = await apiFetch("/api/erp/member-qr/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shopCode: cleanShop, createdBy: "ERP" }),
      });

      setLastQrLink(json.qrLink || "");
      setShopCode(cleanShop);
      setMsg("✅ QR link generated for " + cleanShop);
      await loadQrRows();
    } catch (err) {
      setMsg("❌ " + (err.message || "Generate QR failed"));
    } finally {
      setLoading(false);
    }
  };

  const openInvoiceDetail = async (row) => {
    if (!row?.voucherNo) return;

    setInvoiceLoading(true);
    setMsg("");

    try {
      const json = await apiFetch(
        "/api/erp/member-purchase-history/detail/" +
          encodeURIComponent(row.voucherNo) +
          "?shopCode=" +
          encodeURIComponent(row.shopCode || "")
      );

      setInvoiceDetail({
        sale: json.sale || row,
        items: Array.isArray(json.items) ? json.items : [],
      });
    } catch (err) {
      setMsg("❌ " + (err.message || "Invoice detail failed"));
    } finally {
      setInvoiceLoading(false);
    }
  };

  const closeInvoiceDetail = () => {
    setInvoiceDetail(null);
  };

  const openHistoryForMember = async (member) => {
    const q = member.memberCode || member.phone || member.name || "";
    setHistorySearch(q);
    setActiveTab("history");
    setLoading(true);

    try {
      await loadHistory(q);
    } catch (err) {
      setMsg("❌ " + (err.message || "History load failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="memberMgmtCard">
      <div className="memberMgmtHeader">
        <div>
          <h2>🪪 Member Management</h2>
          <p>Member list, Excel import, shop QR link, address, and purchase history</p>
        </div>

        <button className="memberRefreshBtn" onClick={refreshAll} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {msg ? <div className="memberMsg">{msg}</div> : null}

      <div className="memberTabs">
        <button className={activeTab === "members" ? "active" : ""} onClick={() => setActiveTab("members")}>
          Member List
        </button>
        <button className={activeTab === "import" ? "active" : ""} onClick={() => setActiveTab("import")}>
          Excel Import
        </button>
        <button className={activeTab === "history" ? "active" : ""} onClick={() => setActiveTab("history")}>
          Purchase History
        </button>
        <button className={activeTab === "qr" ? "active" : ""} onClick={() => setActiveTab("qr")}>
          Shop QR
        </button>
      </div>

      {activeTab === "members" ? (
        <div className="memberGrid">
          <form className="memberForm" onSubmit={createMember}>
            <h3>Add Member</h3>

            <label>Member Code</label>
            <input
              value={memberForm.memberCode}
              onChange={(e) => updateMemberForm("memberCode", e.target.value)}
              placeholder="Auto if empty"
            />

            <label>Name</label>
            <input
              value={memberForm.name}
              onChange={(e) => updateMemberForm("name", e.target.value)}
              required
            />

            <label>NRC</label>
            <input
              value={memberForm.nrc}
              onChange={(e) => updateMemberForm("nrc", e.target.value)}
              required
              placeholder="12/YATAYA(N)123456"
            />

            <label>Phone</label>
            <input
              value={memberForm.phone}
              onChange={(e) => updateMemberForm("phone", e.target.value)}
              required
              placeholder="09123456789"
            />

            <label>Verification Email</label>
            <input
              type="email"
              value={memberForm.email}
              onChange={(e) => updateMemberForm("email", e.target.value)}
              required
              placeholder="member@example.com"
            />

            <label>Address</label>
            <input
              value={memberForm.address}
              onChange={(e) => updateMemberForm("address", e.target.value)}
              placeholder="Member address"
            />

            <label>Discount %</label>
            <input
              value={memberForm.discountPercent}
              onChange={(e) => updateMemberForm("discountPercent", e.target.value)}
              type="number"
              min="0"
              max="100"
            />

            <label>Note</label>
            <input
              value={memberForm.note}
              onChange={(e) => updateMemberForm("note", e.target.value)}
            />

            <button type="submit" disabled={loading}>
              + Add Member
            </button>
          </form>

          <div className="memberTableBox">
            <h3>Members ({members.length})</h3>

            <div className="memberTableScroll">
              <table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>NRC</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Address</th>
                    <th>Discount</th>
                    <th>Status</th>
                    <th>History</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {members.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="emptyCell">No members yet</td>
                    </tr>
                  ) : (
                    members.map((m) => (
                      <tr key={m.id}>
                        <td>{m.memberCode}</td>
                        <td>{m.name}</td>
                        <td>{m.nrc}</td>
                        <td>{m.phone}</td>
                        <td>{m.email || "-"}</td>
                        <td>{m.address || "-"}</td>
                        <td>{Number(m.discountPercent || 0)}%</td>
                        <td>
                          <span className={"statusPill " + String(m.status || "").toLowerCase()}>
                            {m.status}
                          </span>
                        </td>
                        <td>
                          <button type="button" className="memberBtn memberBtnGhost" onClick={() => openHistoryForMember(m)}>
                            View
                          </button>
                        </td>
                        <td>
                          <div className="memberActionBtns">
                            <button type="button" className="memberBtn memberBtnEdit" onClick={() => startEditMember(m)}>
                              Edit
                            </button>

                            {m.status === "ACTIVE" ? (
                              <button type="button" className="memberBtn memberBtnDanger" onClick={() => changeStatus(m, "INACTIVE")}>
                                Disable
                              </button>
                            ) : (
                              <button type="button" className="memberBtn memberBtnSuccess" onClick={() => changeStatus(m, "ACTIVE")}>
                                Enable
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {activeTab === "import" ? (
        <div className="memberGrid">
          <div className="memberForm">
            <h3>Excel Format Import</h3>
            <p style={{ fontSize: 13, color: "#64748b" }}>
              Excel ထဲက rows ကို copy လုပ်ပြီး ဒီ box ထဲ paste လုပ်ပါ။ Verification email ပါရမယ်။
            </p>

            <textarea
              value={excelText}
              onChange={(e) => setExcelText(e.target.value)}
              style={{
                width: "100%",
                minHeight: 220,
                border: "1px solid #cbd5e1",
                borderRadius: 12,
                padding: 12,
                fontFamily: "monospace",
              }}
            />

            <button type="button" disabled={loading} onClick={importExcelRows}>
              Import {excelRows.length} Rows
            </button>
          </div>

          <div className="memberTableBox">
            <h3>Preview ({excelRows.length})</h3>
            <div className="memberTableScroll">
              <table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>NRC</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Address</th>
                    <th>Discount</th>
                  </tr>
                </thead>
                <tbody>
                  {excelRows.map((row, index) => (
                    <tr key={index}>
                      <td>{row.memberCode || "Auto"}</td>
                      <td>{row.name}</td>
                      <td>{row.nrc}</td>
                      <td>{row.phone}</td>
                      <td>{row.email}</td>
                      <td>{row.address}</td>
                      <td>{row.discountPercent || 10}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {activeTab === "history" ? (
        <div className="memberTableBox">
          <h3>Member Purchase History</h3>

          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <input
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              placeholder="Search member code / name / phone / voucher / shop"
              style={{
                flex: 1,
                height: 40,
                border: "1px solid #cbd5e1",
                borderRadius: 10,
                padding: "0 12px",
              }}
            />
            <button type="button" onClick={() => loadHistory()} disabled={loading}>
              Search
            </button>
          </div>

          <div className="memberTableScroll">
            <table>
              <thead>
                <tr>
                  <th>Date / Time</th>
                  <th>Shop</th>
                  <th>Voucher</th>
                  <th>Member</th>
                  <th>Phone</th>
                  <th>Address</th>
                  <th>Discount</th>
                  <th>Grand Total</th>
                  <th>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {historyRows.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="emptyCell">No member purchase history</td>
                  </tr>
                ) : (
                  historyRows.map((row, index) => (
                    <tr key={row.voucherNo + "-" + index}>
                      <td>{formatDateTime(row.saleTime || row.createdAt)}</td>
                      <td>{row.shopCode}</td>
                      <td>{row.voucherNo}</td>
                      <td>{row.memberCode || row.memberName || "-"}</td>
                      <td>{row.memberPhone || "-"}</td>
                      <td>{row.address || "-"}</td>
                      <td>{formatMoney(row.memberDiscountAmount)} Ks</td>
                      <td>{formatMoney(row.grandTotal)} Ks</td>
                      <td>
                        <button
                          type="button"
                          className="memberBtn memberBtnPrimary"
                          onClick={() => openInvoiceDetail(row)}
                          disabled={invoiceLoading}
                        >
                          Detail
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {activeTab === "qr" ? (
        <div className="qrGrid">
          <form className="memberForm" onSubmit={generateQr}>
            <h3>Generate Shop QR Link</h3>

            <label>Shop Code</label>
            <input
              value={shopCode}
              onChange={(e) => setShopCode(e.target.value.toUpperCase())}
              placeholder="DNGHT"
              required
            />

            <button type="submit" disabled={loading}>
              Generate / Get QR Link
            </button>

            {lastQrLink ? (
              <div className="qrResult">
                <b>QR Link</b>
                <p>{lastQrLink}</p>
                <div className="qrActions">
                  <a href={lastQrLink} target="_blank" rel="noreferrer">Open Form</a>
                  <button type="button" onClick={() => copyText(lastQrLink)}>Copy</button>
                </div>
              </div>
            ) : null}
          </form>

          <div className="memberTableBox">
            <h3>Shop QR Links ({qrRows.length})</h3>

            <div className="memberTableScroll">
              <table>
                <thead>
                  <tr>
                    <th>Shop</th>
                    <th>Status</th>
                    <th>QR Link</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {qrRows.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="emptyCell">No QR links yet</td>
                    </tr>
                  ) : (
                    qrRows.map((row) => (
                      <tr key={row.id || row.shopCode}>
                        <td>{row.shopCode}</td>
                        <td>
                          <span className={"statusPill " + String(row.status || "").toLowerCase()}>
                            {row.status}
                          </span>
                        </td>
                        <td className="linkCell">{row.qrLink}</td>
                        <td>
                          <div className="qrActions">
                            <a href={row.qrLink} target="_blank" rel="noreferrer">Open</a>
                            <button type="button" onClick={() => copyText(row.qrLink)}>Copy</button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
      {invoiceDetail ? (
        <div className="memberModalOverlay">
          <div className="memberInvoiceModal">
            <div className="memberModalHead">
              <div>
                <h3>Member Invoice Detail</h3>
                <p>
                  {invoiceDetail.sale?.voucherNo} · {invoiceDetail.sale?.shopCode} ·{" "}
                  {formatDateTime(invoiceDetail.sale?.saleTime || invoiceDetail.sale?.createdAt)}
                </p>
              </div>
              <button type="button" className="memberModalClose" onClick={closeInvoiceDetail}>
                ×
              </button>
            </div>

            <div className="memberInvoiceSummary">
              <div>
                <span>Member</span>
                <b>
                  {invoiceDetail.sale?.memberCode ||
                    invoiceDetail.sale?.memberName ||
                    "-"}
                </b>
              </div>
              <div>
                <span>Phone</span>
                <b>{invoiceDetail.sale?.memberPhone || "-"}</b>
              </div>
              <div>
                <span>Address</span>
                <b>{invoiceDetail.sale?.address || "-"}</b>
              </div>
              <div>
                <span>Member Discount</span>
                <b>{formatMoney(invoiceDetail.sale?.memberDiscountAmount)} Ks</b>
              </div>
              <div>
                <span>Grand Total</span>
                <b>{formatMoney(invoiceDetail.sale?.grandTotal)} Ks</b>
              </div>
            </div>

            <div className="memberTableScroll">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Color</th>
                    <th>Size</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Disc %</th>
                    <th>Discount</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceDetail.items.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="emptyCell">
                        No item detail found
                      </td>
                    </tr>
                  ) : (
                    invoiceDetail.items.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{item.code || "-"}</td>
                        <td>{item.name || "-"}</td>
                        <td>{item.color || "-"}</td>
                        <td>{item.size || "-"}</td>
                        <td>{formatMoney(item.qty)}</td>
                        <td>{formatMoney(item.price)} Ks</td>
                        <td>{Number(item.discountPercent || 0)}%</td>
                        <td>{formatMoney(item.discountAmount)} Ks</td>
                        <td>{formatMoney(item.amount)} Ks</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="memberInvoiceTotals">
              <div><span>Total</span><b>{formatMoney(invoiceDetail.sale?.totalAmount)} Ks</b></div>
              <div><span>Item Discount</span><b>{formatMoney(invoiceDetail.sale?.itemDiscountAmount)} Ks</b></div>
              <div><span>Member Discount</span><b>{formatMoney(invoiceDetail.sale?.memberDiscountAmount)} Ks</b></div>
              <div><span>Coupon</span><b>{formatMoney(invoiceDetail.sale?.couponAmount)} Ks</b></div>
              <div><span>Delivery</span><b>{formatMoney(invoiceDetail.sale?.deliveryCharge)} Ks</b></div>
              <div className="grand"><span>Grand Total</span><b>{formatMoney(invoiceDetail.sale?.grandTotal)} Ks</b></div>
            </div>
          </div>
        </div>
      ) : null}

      {editingMember ? (
        <div className="memberModalOverlay">
          <form className="memberModal" onSubmit={saveEditMember}>
            <div className="memberModalHead">
              <div>
                <h3>Edit Member</h3>
                <p>{editingMember.memberCode || editingMember.name}</p>
              </div>
              <button type="button" className="memberModalClose" onClick={cancelEditMember}>
                ×
              </button>
            </div>

            <div className="memberModalGrid">
              <label>
                Member Code
                <input
                  value={editForm.memberCode}
                  onChange={(e) => updateEditForm("memberCode", e.target.value)}
                  required
                />
              </label>

              <label>
                Name
                <input
                  value={editForm.name}
                  onChange={(e) => updateEditForm("name", e.target.value)}
                  required
                />
              </label>

              <label>
                NRC
                <input
                  value={editForm.nrc}
                  onChange={(e) => updateEditForm("nrc", e.target.value)}
                  required
                />
              </label>

              <label>
                Phone
                <input
                  value={editForm.phone}
                  onChange={(e) => updateEditForm("phone", e.target.value)}
                  required
                />
              </label>

              <label>
                Verification Email
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => updateEditForm("email", e.target.value)}
                  required
                />
              </label>

              <label className="memberModalFull">
                Address
                <input
                  value={editForm.address}
                  onChange={(e) => updateEditForm("address", e.target.value)}
                  placeholder="Member address"
                />
              </label>

              <label>
                Discount %
                <input
                  value={editForm.discountPercent}
                  onChange={(e) => updateEditForm("discountPercent", e.target.value)}
                  type="number"
                  min="0"
                  max="100"
                />
              </label>

              <label>
                Note
                <input
                  value={editForm.note}
                  onChange={(e) => updateEditForm("note", e.target.value)}
                />
              </label>
            </div>

            <div className="memberModalActions">
              <button type="button" className="memberBtn memberBtnGhost" onClick={cancelEditMember}>
                Cancel
              </button>
              <button type="submit" className="memberBtn memberBtnPrimary" disabled={loading}>
                Save Changes
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </section>
  );
}

